import { LifecycleState, PurchaseRequestState, RegulationStatus } from '@vendasta/protos/sms/v2/enums';
import type { PhoneProvisioningInfo } from '@vendasta/protos/sms/v2/types';
import { Status } from '../progress-tracker/progress-tracker.component';

const STATUS_KEY = 'SMS_REGULATION.COMMON.STATUS';

/**
 * Maps the latest PhoneProvisioningInfo row to the 4th stepper stage status for IOL.
 * The stage is considered "Not Started" until the regulation is approved.
 */
export function getPhoneProvisioningStageStatus(
  provisioninginfo: PhoneProvisioningInfo | null,
  regulationStatus: RegulationStatus,
): Status {
  if (regulationStatus !== RegulationStatus.REGULATION_STATUS_APPROVED) {
    return Status.NotStarted;
  }
  // regulationStatus approved but the provisioning orchestrator hasn't created a row yet.
  if (!provisioninginfo) {
    return Status.InProgress;
  }

  switch (provisioninginfo.purchaseRequestState) {
    case PurchaseRequestState.PURCHASE_REQUEST_STATE_PROVISIONED:
      // Provisioned, but only "done" when it's the current/active number.
      return provisioninginfo.lifecycleState === LifecycleState.LIFECYCLE_STATE_ACTIVE
        ? Status.Completed
        : Status.InProgress;
    case PurchaseRequestState.PURCHASE_REQUEST_STATE_FAILED:
      return Status.Failed;
    case PurchaseRequestState.PURCHASE_REQUEST_STATE_NOT_STARTED:
    case PurchaseRequestState.PURCHASE_REQUEST_STATE_REQUESTED:
    default:
      return Status.InProgress;
  }
}

/**
 * i18n key for the approved-state banner message that corresponds to the 4th-step status.
 * Returns null when the step-4 status doesn't drive the banner (e.g. NotStarted).
 */
export function getPhoneProvisioningMessageKey(stageStatus: Status): string | null {
  switch (stageStatus) {
    case Status.InProgress:
      return `${STATUS_KEY}.NUMBER_REQUESTING_MESSAGE`;
    case Status.Completed:
      return `${STATUS_KEY}.NUMBER_ACTIVE_MESSAGE`;
    case Status.Failed:
      return `${STATUS_KEY}.NUMBER_FAILED_MESSAGE`;
    case Status.NotStarted:
    default:
      return null;
  }
}
